import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Avatar, AvatarFallback } from './ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { toast } from 'sonner';
import {
  Mic,
  MicOff,
  PhoneOff,
  Users,
  Plus,
  Volume2,
  VolumeX,
  Radio,
  Hash,
  Settings,
  LogOut,
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Discord-style Voice Channels
 * Uses LiveKit for multi-user voice communication
 */

// Voice Channel List Component
export const VoiceChannelList = ({ onJoinChannel }) => {
  const [channels, setChannels] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newChannelName, setNewChannelName] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchChannels = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/voice-channels`);
      setChannels(res.data);
    } catch (error) {
      console.error('Failed to fetch voice channels:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchChannels();
    // Refresh every 10 seconds
    const interval = setInterval(fetchChannels, 10000);
    return () => clearInterval(interval);
  }, [fetchChannels]);

  const handleCreateChannel = async () => {
    if (!newChannelName.trim()) {
      toast.error('Please enter a channel name');
      return;
    }

    try {
      const res = await axios.post(`${API}/voice-channels`, {
        name: newChannelName,
        max_participants: 20,
      });
      setChannels(prev => [res.data, ...prev]);
      setNewChannelName('');
      setShowCreateDialog(false);
      toast.success('Voice channel created!');
    } catch (error) {
      toast.error('Failed to create channel');
    }
  };

  return (
    <div className="voice-channel-list">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <div className="flex items-center gap-2">
          <Radio className="w-4 h-4 text-primary" />
          <span className="text-sm font-medium text-slate-300">Voice Channels</span>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6"
          onClick={() => setShowCreateDialog(true)}
          data-testid="create-voice-channel-btn"
        >
          <Plus className="w-4 h-4" />
        </Button>
      </div>

      <ScrollArea className="h-64">
        <div className="p-2 space-y-1">
          {loading ? (
            <p className="text-center text-sm text-slate-500 py-4">Loading...</p>
          ) : channels.length === 0 ? (
            <p className="text-center text-sm text-slate-500 py-4">No voice channels</p>
          ) : (
            channels.map(channel => (
              <VoiceChannelItem
                key={channel.id}
                channel={channel}
                onJoin={() => onJoinChannel(channel)}
              />
            ))
          )}
        </div>
      </ScrollArea>

      {/* Create Channel Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle>Create Voice Channel</DialogTitle>
            <DialogDescription>
              Create a new voice channel for group conversations
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <Input
              value={newChannelName}
              onChange={(e) => setNewChannelName(e.target.value)}
              placeholder="Channel name"
              data-testid="voice-channel-name-input"
            />
            <Button
              onClick={handleCreateChannel}
              className="w-full"
              data-testid="create-voice-channel-submit"
            >
              Create Channel
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Individual Voice Channel Item
const VoiceChannelItem = ({ channel, onJoin }) => {
  return (
    <button
      onClick={onJoin}
      className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-secondary/50 transition-colors group"
      data-testid={`voice-channel-${channel.id}`}
    >
      <Volume2 className="w-4 h-4 text-slate-400 group-hover:text-primary" />
      <div className="flex-1 text-left">
        <p className="text-sm text-slate-300 group-hover:text-white">
          {channel.name}
        </p>
        {channel.current_participants > 0 && (
          <div className="flex items-center gap-1 text-xs text-emerald-400">
            <Users className="w-3 h-3" />
            <span>{channel.current_participants} connected</span>
          </div>
        )}
      </div>
    </button>
  );
};

// Voice Channel Room Component (Active Call UI)
export const VoiceChannelRoom = ({ channel, onLeave, participants = [] }) => {
  const [isMuted, setIsMuted] = useState(false);
  const [isDeafened, setIsDeafened] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const [connectedUsers, setConnectedUsers] = useState([]);
  const localStreamRef = useRef(null);
  const audioContextRef = useRef(null);

  // Initialize audio
  useEffect(() => {
    const initAudio = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        localStreamRef.current = stream;
        setConnectionStatus('connected');
        toast.success(`Joined ${channel.name}`);
      } catch (error) {
        console.error('Failed to get microphone:', error);
        toast.error('Failed to access microphone');
        setConnectionStatus('error');
      }
    };

    initAudio();

    return () => {
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, [channel.name]);

  const toggleMute = () => {
    if (localStreamRef.current) {
      localStreamRef.current.getAudioTracks().forEach(track => {
        track.enabled = isMuted;
      });
      setIsMuted(!isMuted);
    }
  };

  const toggleDeafen = () => {
    setIsDeafened(!isDeafened);
    // In a real implementation, this would mute all incoming audio
  };

  const handleLeave = () => {
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
    }
    onLeave();
  };

  return (
    <div className="voice-channel-room fixed bottom-20 left-4 w-72 bg-card border border-border rounded-xl shadow-2xl overflow-hidden z-50">
      {/* Header */}
      <div className="bg-emerald-500/10 border-b border-emerald-500/30 px-4 py-3">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
          <span className="text-sm font-medium text-emerald-400">Voice Connected</span>
        </div>
        <p className="text-xs text-slate-400 mt-1">{channel.name}</p>
      </div>

      {/* Participants */}
      <div className="p-3">
        <ScrollArea className="h-32">
          <div className="space-y-2">
            {/* Local User */}
            <div className="flex items-center gap-3 p-2 rounded-lg bg-secondary/30">
              <Avatar className="h-8 w-8">
                <AvatarFallback className="bg-primary/20 text-primary text-xs">
                  You
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <p className="text-sm text-white">You</p>
                <p className="text-xs text-slate-500">
                  {isMuted ? 'Muted' : 'Speaking'}
                </p>
              </div>
              {isMuted ? (
                <MicOff className="w-4 h-4 text-red-400" />
              ) : (
                <Mic className="w-4 h-4 text-emerald-400" />
              )}
            </div>

            {/* Other Participants (placeholder for LiveKit integration) */}
            {connectedUsers.map(user => (
              <div key={user.id} className="flex items-center gap-3 p-2 rounded-lg">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-secondary text-xs">
                    {user.name?.[0]?.toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <p className="text-sm text-slate-300">{user.name}</p>
                </div>
                <Volume2 className="w-4 h-4 text-slate-400" />
              </div>
            ))}
          </div>
        </ScrollArea>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-center gap-2 p-3 border-t border-border bg-secondary/20">
        <Button
          variant={isMuted ? 'destructive' : 'secondary'}
          size="icon"
          className="h-10 w-10 rounded-full"
          onClick={toggleMute}
          data-testid="voice-mute-btn"
        >
          {isMuted ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
        </Button>
        
        <Button
          variant={isDeafened ? 'destructive' : 'secondary'}
          size="icon"
          className="h-10 w-10 rounded-full"
          onClick={toggleDeafen}
          data-testid="voice-deafen-btn"
        >
          {isDeafened ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
        </Button>

        <Button
          variant="destructive"
          size="icon"
          className="h-10 w-10 rounded-full"
          onClick={handleLeave}
          data-testid="voice-leave-btn"
        >
          <PhoneOff className="w-4 h-4" />
        </Button>
      </div>

      {/* Connection Status */}
      {connectionStatus === 'connecting' && (
        <div className="absolute inset-0 bg-card/90 flex items-center justify-center">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2" />
            <p className="text-sm text-slate-400">Connecting...</p>
          </div>
        </div>
      )}

      {connectionStatus === 'error' && (
        <div className="absolute inset-0 bg-card/90 flex items-center justify-center">
          <div className="text-center p-4">
            <p className="text-sm text-red-400 mb-2">Connection failed</p>
            <Button variant="outline" size="sm" onClick={handleLeave}>
              Close
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

// Mini voice status indicator for sidebar
export const VoiceStatusIndicator = ({ channel, onDisconnect }) => {
  if (!channel) return null;

  return (
    <div className="voice-status-indicator p-2 mx-2 mb-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
          <div>
            <p className="text-xs font-medium text-emerald-400">Voice Connected</p>
            <p className="text-xs text-slate-500 truncate max-w-[120px]">{channel.name}</p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 text-slate-400 hover:text-red-400"
          onClick={onDisconnect}
        >
          <PhoneOff className="w-3 h-3" />
        </Button>
      </div>
    </div>
  );
};

export default VoiceChannelList;
